import Enumerable from "linq";
import {
  IItem,
  IValueContainable,
  AbstractDrawableValuedItem,
  instanceOfIFontSizeAutoAdjustable,
  instanceOfIHisInformationApplicable,
  TextBoxItem,
  MovableTextBoxItem,
  AbstractPlainTextItem,
} from "../../../../../../../domain/document/items";
import { User } from "../../../../../../../domain/user";
import { Department } from "../../../../../../../domain/department";
import { HisItemParameter, ScreenKinds, HisItemBehaviorFactory } from "../../../../../../../domain/his/behavior";
import { HisInformationUserGroupSource } from "../../../../../../../domain/his/groupInput/HisInformationGroupSource";
import { HisItemSelectAction } from "../../../../../../../domain/his/action";
import { IDocumentEditable } from "../../../../../../document/editor";
import { IValueAccessible } from "../IValueAccessible";
import { AbstractValueAccessor, AbstractItemValueAccessor } from "./AbstractValueAccessor";

export class UsersValueAccessor extends AbstractValueAccessor<User[]> {
  constructor(source: IItem, readonly document: IDocumentEditable, readonly department: Department, readonly itemSetId: number) {
    super(UsersValueAccessor._createAccessor(source, document, department, itemSetId));
  }

  private static _createAccessor(source: IItem, document: IDocumentEditable, department: Department, itemSetId: number): IValueAccessible<User[]> {
    const hisItemParameter = this.getHisItemParameter(source);
    if (source instanceof TextBoxItem) {
      if (hisItemParameter && hisItemParameter.screenKind === ScreenKinds.Doctor) {
        return new UserSelectableHisItemValueAccessor(source, document, department, itemSetId, hisItemParameter);
      }
      return new TextBoxItemFreeTextAccessor(source);
    } else if (source instanceof MovableTextBoxItem) {
      return new MovableTextBoxItemFreeTextAccessor(source);
    }
    throw new Error("対象外オブジェクト");
  }

  private static  getHisItemParameter(source: IItem): HisItemParameter {
    return instanceOfIHisInformationApplicable(source)
      ? source.hisItemParameter
      : null;
  }
}

abstract class AbstractUsersValueAccessor<T extends IItem & IValueContainable> extends AbstractItemValueAccessor<T, User[]> {
  constructor(source: T) {
    super(source);
  }

  protected _set(source: T, value: User[]): void {
    this.__set(source, value);
    if (source instanceof AbstractDrawableValuedItem) { // TODO: リファクタ.適切なinterfaceを定義すべき.
      source.adjustObjectHeight();
    }
    if (instanceOfIFontSizeAutoAdjustable(source)) {
      source.adjustFontSize();
    }
  }

  protected abstract __set(source: T, value: User[]): void;
}

class UserSelectableHisItemValueAccessor extends AbstractUsersValueAccessor<TextBoxItem> {
  constructor(source: TextBoxItem, private readonly document: IDocumentEditable, private readonly department: Department, private readonly itemSetId: number, private readonly hisItemParameter: HisItemParameter) {
    super(source);
    this._users = null;
  }

  protected _validate(source: TextBoxItem): void {}
  protected _users: User[];

  protected _get(source: TextBoxItem): User[] {
    return this._users;
  }

  protected __set(source: TextBoxItem, value: User[]): void {
    const action = new HisItemSelectAction(this.document, source, new HisInformationUserGroupSource(value, this.department), this.itemSetId);
    const behavior = HisItemBehaviorFactory.create(this.hisItemParameter.key, action.dateFormat, this.hisItemParameter.objectKind);
    const hisInfoValue = behavior.getValue(action.informationItems);
    source.setHisInfoItems(hisInfoValue, false);
    source.setSelectedItems((!value || value.length === 0) ? null : value.concat());
    this._users = value;
  }
}

abstract class AbstractTextBoxItemValueAccessor<T extends AbstractPlainTextItem> extends AbstractUsersValueAccessor<T> {
  constructor(source: T) {
    super(source);
    this._users = null;
  }

  protected _users: User[];

  protected _validate(source: T): void {
    if (!source.isFreeInputable) {
      throw new Error("非フリー入力");
    }
  }

  protected _get(source: T): User[] {
    return this._users;
  }

  protected __set(source: T, value: User[]): void {
    if (!source.shouldInputFree) {
      source.shouldInputFree = true;
    }
    source.value = this.toStringValue(value);
    this._users = value;
  }

  protected toStringValue(value: User[]): string {
    return value
      ? Enumerable.from(value).select(u => u.name).toArray().join("・")
      : "";
  }
}

class TextBoxItemFreeTextAccessor extends AbstractTextBoxItemValueAccessor<TextBoxItem> {
  constructor(source: TextBoxItem) {
    super(source);
  }

  protected _validate(source: TextBoxItem): void {
    super._validate(source);
    if (source.isNumeric) {
      throw new Error("数値入力");
    }
  }
}

class MovableTextBoxItemFreeTextAccessor extends AbstractTextBoxItemValueAccessor<MovableTextBoxItem> {
  constructor(source: MovableTextBoxItem) {
    super(source);
  }
}
